import { destination, type DestinationId } from "@/lib/destinations";
import { usePhone } from "@/lib/usePhone";

import { Mark } from "./Mark";

interface TopBarProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  /**
   * Which destination this is, so the phone form can name it in Japanese and carry the mark.
   *
   * Optional: a surface that has its own phone chrome (Review during a sitting, Read's tab strip)
   * passes nothing and keeps the desktop bar at every width.
   */
  destinationId?: DestinationId;
  /**
   * The phone bar's trailing actions — up to two 44×44 targets.
   *
   * Separate from `children` because they are not the same thing: `children` is a row of counters
   * and dividers, which is desktop furniture and does not fit 390px. The frames draw one or two
   * icon targets instead (`⌕` on 復習, `Vocab ▾` on the stats tabs).
   */
  phoneActions?: React.ReactNode;
  /**
   * Whether the phone bar carries the 語 mark.
   *
   * **Off everywhere but the 復習 landing**, which is what the Mobile canvas draws: one frame of six
   * has the mark and the other five open with the destination word alone. It reads as deliberate
   * rather than as a slip at that ratio — the mark says *which app*, which is worth a tile on the
   * screen you land on and is noise on the four you reach from a tab bar that is already showing
   * you where you are.
   *
   * A prop rather than `destinationId === "review"` inside this component, so the decision reads at
   * the surface that makes it instead of as a name-check in the shell.
   */
  mark?: boolean;
}

/**
 * The chrome every surface carries — **50px on the desktop, 44px on a phone, and the phone form is
 * a different bar rather than the same one squeezed.**
 *
 * The phone version is read off `docs/designs/Nihongonator - Mobile.dc.html`, which draws it
 * identically on all nine frames: the 22px mark, then the destination as a Japanese word at 16px,
 * then up to two 44×44 targets. The app had no phone top bar at all — every surface rendered the
 * 50px desktop one, whose right-hand side is a row of counters that does not fit 390px.
 *
 * **It is not redundant with the tab bar.** The Mobile canvas says so directly, of the one frame
 * where you might argue: *"復習 · the landing keeps its top bar here, because the tab bar is not a
 * title."* The bar at the bottom says where you can go; this says where you are.
 *
 * The trailing targets grow into the bar's own 16px gutter with a negative margin, so the title
 * does not move and only the hit area changes — `Session`'s phone chrome already establishes that,
 * for the same reason (Alex, 11 Aug 2026: the corner glyphs were "extremely hard to tap").
 */
export function TopBar({
  title,
  subtitle,
  children,
  destinationId,
  phoneActions,
  mark = false,
}: TopBarProps) {
  const isPhone = usePhone();

  if (isPhone && destinationId) {
    return (
      <header className="flex h-[44px] shrink-0 items-center gap-[9px] border-b border-line px-[16px] box-border">
        {mark && <Mark size={22} />}
        <span className="shrink-0 font-jp text-[18px]/[1.5] font-bold tracking-[.03em] text-ink-bright">
          {destination(destinationId).word}
        </span>
        {/* The subtitle survives on the phone where `children` does not: the frames draw `第三章`
            beside 読書 and `Vocab` beside 単語, and it is the one piece of the desktop bar's right
            side that answers *which* rather than *how many*. */}
        {subtitle && (
          <span className="min-w-0 truncate text-secondary text-ink-3">{subtitle}</span>
        )}
        <div className="flex-1" />
        {phoneActions && <span className="-mr-[12px] flex items-center">{phoneActions}</span>}
      </header>
    );
  }

  return (
    <header className="flex h-[var(--topbar-h)] shrink-0 items-center gap-[11px] border-b border-line bg-panel px-[18px] box-border">
      <span className="text-title font-semibold text-ink">{title}</span>
      {subtitle && <span className="text-secondary text-ink-4">{subtitle}</span>}
      <div className="flex-1" />
      {children}
    </header>
  );
}

/** A vertical hairline between top-bar counters. */
export function TopBarDivider() {
  return <span aria-hidden className="h-4 w-px bg-line" />;
}

/**
 * A 44px trailing target for the phone bar.
 *
 * A component rather than a class string because the size is the whole point: these are the two
 * controls furthest into the corner of the screen, which is where a thumb is least accurate.
 */
export function TopBarAction({
  label,
  onClick,
  children,
  pressed,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  /** A toggle's state. Undefined for a plain action, so it never claims `aria-pressed`. */
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className={[
        "flex h-11 w-11 items-center justify-center text-[17px]/[1]",
        pressed ? "text-[color:var(--accent-text)]" : "text-ink-3",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
